import { getCurrentUserId } from 'src/services/secureServiceWrapper';
import { sanitizeInput } from 'src/utils/security';
import { getSupabase } from 'src/utils/supabase';

export interface MaintenanceWindow {
  id: string;
  scheduled_start: string;
  scheduled_end: string;
  notice_message: string | null;
  maintenance_message: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface MaintenanceWindowInput {
  scheduled_start: string;
  scheduled_end: string;
  notice_message?: string | null;
  maintenance_message?: string | null;
}

const TABLE = 'maintenance_windows';
const FUNCTION_NAME = 'admin-maintenance';

const sanitizeMessage = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const trimmed = sanitizeInput(value.trim());
  return trimmed || null;
};

async function invokeAdminAction(
  action: string,
  payload: Record<string, unknown>
): Promise<{ window?: MaintenanceWindow }> {
  const supabase = await getSupabase();
  const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
    body: { action, ...payload },
  });
  if (error) throw new Error('Maintenance action failed');
  const response = data as { success?: boolean; data?: { window?: MaintenanceWindow }; error?: string } | null;
  if (!response?.success) throw new Error(response?.error || 'Maintenance action failed');
  return response.data ?? {};
}

export const maintenanceService = {
  async getCurrent(): Promise<MaintenanceWindow | null> {
    const supabase = await getSupabase();

    const { data: active, error: activeErr } = await supabase
      .from(TABLE)
      .select('*')
      .eq('is_active', true)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (activeErr) throw new Error('Unable to load maintenance status');
    if (active) return active as unknown as MaintenanceWindow;

    const nowIso = new Date().toISOString();
    const { data: upcoming, error: upErr } = await supabase
      .from(TABLE)
      .select('*')
      .gte('scheduled_end', nowIso)
      .order('scheduled_start', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (upErr) throw new Error('Unable to load maintenance status');
    return (upcoming as unknown as MaintenanceWindow) ?? null;
  },

  async listAll(): Promise<MaintenanceWindow[]> {
    const supabase = await getSupabase();
    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .order('scheduled_start', { ascending: false })
      .limit(50);

    if (error) throw new Error('Unable to load maintenance windows');
    return (data ?? []) as unknown as MaintenanceWindow[];
  },

  async create(input: MaintenanceWindowInput): Promise<MaintenanceWindow> {
    const start = new Date(input.scheduled_start);
    const end = new Date(input.scheduled_end);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      throw new Error('Invalid start or end time');
    }
    if (end <= start) throw new Error('End time must be after start time');

    const result = await invokeAdminAction('create_window', {
      scheduledStart: start.toISOString(),
      scheduledEnd: end.toISOString(),
      noticeMessage: sanitizeMessage(input.notice_message),
      maintenanceMessage: sanitizeMessage(input.maintenance_message),
    });

    if (!result.window) throw new Error('Unable to schedule maintenance window');
    return result.window;
  },

  async update(id: string, input: Partial<MaintenanceWindowInput>): Promise<MaintenanceWindow> {
    if (!id) throw new Error('Maintenance window id is required');

    const payload: Record<string, unknown> = { id };
    if (input.scheduled_start) {
      const start = new Date(input.scheduled_start);
      if (Number.isNaN(start.getTime())) throw new Error('Invalid start time');
      payload.scheduledStart = start.toISOString();
    }
    if (input.scheduled_end) {
      const end = new Date(input.scheduled_end);
      if (Number.isNaN(end.getTime())) throw new Error('Invalid end time');
      payload.scheduledEnd = end.toISOString();
    }
    if (input.notice_message !== undefined) payload.noticeMessage = sanitizeMessage(input.notice_message);
    if (input.maintenance_message !== undefined) {
      payload.maintenanceMessage = sanitizeMessage(input.maintenance_message);
    }

    const result = await invokeAdminAction('update_window', payload);
    if (!result.window) throw new Error('Unable to update maintenance window');
    return result.window;
  },

  async setActive(id: string, active: boolean): Promise<MaintenanceWindow> {
    if (!id) throw new Error('Maintenance window id is required');
    const result = await invokeAdminAction('set_active', { id, active });
    if (!result.window) throw new Error('Unable to update maintenance status');
    return result.window;
  },

  async remove(id: string): Promise<void> {
    if (!id) throw new Error('Maintenance window id is required');
    await invokeAdminAction('delete_window', { id });
  },

  async subscribe(callback: () => void): Promise<() => void> {
    const currentUserId = await getCurrentUserId();
    if (!currentUserId) return () => {};

    const supabase = await getSupabase();
    const channel = supabase
      .channel(`maintenance_windows_changes_${currentUserId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: TABLE }, () => {
        callback();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },
};
